import { Auth } from 'aws-amplify';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';

import { StringOrNull } from './apiTypes/misc';
import avatarMiddleware from './avatarMiddleware';
import maintenanceMiddleware from './maintenanceMiddleware';

// TODO: Check if any endpoints need root `response` object.

let VERBOSE = false;

const debugLogger = {
  log: (...params: any[]) => {
    if (VERBOSE) {
      /** biome-ignore lint/suspicious/noConsole: debug logging */
      console.log('[API]', ...params);
    }
  },
};

let instance: AxiosInstance | null = null;
let onUnauthenticatedDefault: (() => Promise<void> | void) | null = null;

type InitApiParams = {
  verbose?: boolean;
  onUnauthenticated?: () => Promise<void> | void;
} & AxiosRequestConfig;

export function init({ verbose = false, onUnauthenticated, ...axiosParams }: InitApiParams) {
  VERBOSE = verbose;
  onUnauthenticatedDefault = onUnauthenticated ?? null;
  instance = axios.create(axiosParams);

  avatarMiddleware(instance);
  maintenanceMiddleware(instance);

  return instance;
}

export function getAxiosInstance(): AxiosInstance {
  if (!instance) throw new Error('Api must be initialized with init() function first');

  return instance;
}

function instanceOfAxiosError(error: any): error is AxiosError {
  return 'response' in error;
}

export async function getAuthToken() {
  try {
    const session = await Auth.currentSession();
    const idToken = session.getIdToken();
    const jwtToken = idToken.getJwtToken();

    return `Bearer ${jwtToken}`;
  } catch (_error) {
    return '';
  }
}

type RequestConfig = { onUnauthenticated?: () => Promise<void> | void } & AxiosRequestConfig;

export async function get(endpoint: string, config: RequestConfig = {}): Promise<any> {
  const { onUnauthenticated, ...axiosConfig } = config;
  try {
    debugLogger.log(`GET '${endpoint}'`, config);

    const Authorization = await getAuthToken();

    const response = await getAxiosInstance()({
      url: endpoint,
      method: 'get',
      ...axiosConfig,
      headers: { Authorization },
    });

    debugLogger.log(`GET '${endpoint}' response:`, response.data);
    return response.data;
  } catch (error) {
    return await handleApiError({
      error,
      retry: () => get(endpoint, config),
      onUnauthenticated,
    });
  }
}

export async function post(endpoint: string, data: any, config: RequestConfig = {}): Promise<any> {
  const { onUnauthenticated, ...axiosConfig } = config;
  try {
    debugLogger.log(`POST '${endpoint}':`, data);

    const Authorization = await getAuthToken();

    const response = await getAxiosInstance()({
      url: endpoint,
      method: 'post',
      responseType: 'json',
      data,
      ...axiosConfig,
      headers: { Authorization },
    });

    debugLogger.log(`POST '${endpoint}' response:`, response.data);
    return response.data;
  } catch (error) {
    return await handleApiError({
      error,
      retry: () => post(endpoint, data, config),
      onUnauthenticated,
    });
  }
}

export async function put(endpoint: string, data: any, config: RequestConfig = {}): Promise<any> {
  const { onUnauthenticated, ...axiosConfig } = config;

  try {
    debugLogger.log(`PUT '${endpoint}':`, data);

    const Authorization = await getAuthToken();

    const response = await getAxiosInstance()({
      url: endpoint,
      method: 'put',
      responseType: 'json',
      data,
      ...axiosConfig,
      headers: { Authorization },
    });

    debugLogger.log(`PUT '${endpoint}' response:`, response.data);
    return response.data;
  } catch (error) {
    return await handleApiError({
      error,
      retry: () => put(endpoint, data, config),
      onUnauthenticated,
    });
  }
}

export async function patch(endpoint: string, data: any, config: RequestConfig = {}): Promise<any> {
  const { onUnauthenticated, ...axiosConfig } = config;

  try {
    debugLogger.log(`PATCH '${endpoint}':`, data);

    const Authorization = await getAuthToken();

    const response = await getAxiosInstance()({
      url: endpoint,
      method: 'patch',
      responseType: 'json',
      data,
      ...axiosConfig,
      headers: { Authorization },
    });

    debugLogger.log(`PATCH '${endpoint}' response:`, response.data);
    return response.data;
  } catch (error) {
    return await handleApiError({
      error,
      retry: () => patch(endpoint, data, config),
      onUnauthenticated,
    });
  }
}

export async function remove(endpoint: string, data?: any, config: RequestConfig = {}) {
  const { onUnauthenticated, ...axiosConfig } = config;

  try {
    debugLogger.log(`DELETE '${endpoint}':`, data);

    const Authorization = await getAuthToken();

    const response = await getAxiosInstance()({
      url: endpoint,
      method: 'delete',
      responseType: 'json',
      data,
      ...axiosConfig,
      headers: { Authorization },
    });

    debugLogger.log(`DELETE '${endpoint}' response:`, response.data);
    return response.data;
  } catch (error) {
    return await handleApiError({
      error,
      retry: () => post(endpoint, data, config),
      onUnauthenticated,
    });
  }
}

export async function getPrivateImage(
  endpoint: StringOrNull,
  config: RequestConfig = {},
): Promise<any> {
  if (!endpoint) return;
  const { onUnauthenticated, ...axiosConfig } = config;

  try {
    debugLogger.log(`GET '${endpoint}'`, config);

    const Authorization = await getAuthToken();

    const response = await getAxiosInstance()({
      url: endpoint,
      method: 'get',
      ...axiosConfig,
      responseType: 'blob',
      headers: { Authorization, Accept: 'image/*' },
    });

    debugLogger.log(`GET '${endpoint}' response:`, response);

    return URL.createObjectURL(response.data as Blob);
  } catch (error) {
    return await handleApiError({
      error,
      retry: () => getPrivateImage(endpoint, config),
      onUnauthenticated,
    });
  }
}

const NETWORK_AWAIT_TIMEOUT = 5000;

type HandleApiErrorParams = {
  error: unknown;
  onUnauthenticated?: () => Promise<void> | void;
  retry: () => Promise<any>;
};

async function handleApiError({ error, onUnauthenticated, retry }: HandleApiErrorParams) {
  if (axios.isCancel(error)) {
    return Promise.resolve(undefined);
  }

  debugLogger.log('[HANDLE API ERROR]', error);

  const handleUnauthenticated = async () => {
    debugLogger.log('[HANDLE API ERROR] handled unauthenticated');
    onUnauthenticated ? await onUnauthenticated() : await onUnauthenticatedDefault?.();
    return Promise.reject(error);
  };

  if (instanceOfAxiosError(error)) {
    switch (error.code) {
      case 'ERR_NETWORK':
        await awaitTimeout(NETWORK_AWAIT_TIMEOUT);
        return await retry();
      case 'ERR_BAD_REQUEST':
        {
          if (error.response?.status === 401) {
            return await handleUnauthenticated();
          }
        }
        break;
      case '401':
        return await handleUnauthenticated();
    }

    return Promise.reject(error.response ? error.response.data : error.response);
  }

  return Promise.reject(error);
}

export const awaitTimeout = (v = 0) =>
  new Promise((res) => {
    if (!v) {
      res(true);
      return;
    }

    setTimeout(res, v);
  });
